/**
 * The booking detail's display half (rd-bookingdetail; web-account-pages D8): the
 * status pill, the pinned hero, the rising sheet with its rows, How to prepare,
 * About, the chat row, the cancel link and window, the errand bar and the cancel
 * feedback. It takes props and reads nothing: the Mini App's BookingDetailPage
 * feeds it from its own reads, the studio website's phone booking page from the
 * website session's.
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/booking-detail.html.
 */
import type { ComponentType, ReactNode } from 'react';
import { virtualPlatformLabel } from '@telify/shared/browser';
import { BookingHero } from '../../components/booking/BookingHero';
import { CalendarMenu, type CalendarChoices } from '../../components/booking/CalendarMenu';
import { FocusedScreenFrame, FocusedScreenMessage, type FocusedScreenFrameProps } from '../../components/booking/FocusedScreenFrame';
import { SafeRichText } from '../../components/booking/SafeRichText';
import { ChatRow } from '../../components/client/ChatRow';
import { ClientSheet, clientSheetStyles as sheetStyles } from '../../components/client/ClientSheet';
import { formatCurrency } from '../../components/payment/formatCurrency';
import { phoneCallStep } from '../booking/shared/bookingRules';
import type { BookingDetail } from '../../types/bookingDetail';
import { BOOKING_READ_FAILED, bookingNotice, derivePaymentRow, fmtDayDate, fmtTime, heroWhenLabel, isRequest, statusPill, type CancelKind } from './bookingDetailRows';
import styles from './BookingDetailPage.module.css';

// ── Icons (mockup SVGs) ──────────────────────────────────────────────────────
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
);
const IconPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
);
const IconVideo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
);
const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
);
const IconCard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
);
const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
);
const IconCopy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
);
const IconChevron = () => (
  <svg className={styles.chev} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
);
const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);
const IconInfo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
);
const IconRefund = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
);

// ── Copy ─────────────────────────────────────────────────────────────────────
/** "Zoom · tap to join"; Other has no display name and reads "tap to join". */
function joinSub(platform: string | null): string {
  const label = virtualPlatformLabel(platform);
  return label ? `${label} · tap to join` : 'tap to join';
}

/**
 * The booking page's frame: its container and floating Back on the focused-screen frame. The Mini
 * App passes its FocusedScreenShell (the frame with its Telegram parts); the website the plain frame.
 */
export function BookingDetailFrame({
  onBack,
  children,
  frame: Frame = FocusedScreenFrame,
}: {
  onBack: () => void;
  children: ReactNode;
  frame?: ComponentType<FocusedScreenFrameProps>;
}) {
  return (
    <Frame className={styles.page} testId="booking-detail-page" onBack={onBack}>
      {children}
    </Frame>
  );
}

/**
 * The booking page when its read failed: not found (the booking was removed, or is not this
 * client's) offers Back; any other failure offers Try again. The studio website shows its own
 * not-found page instead of the first.
 */
export function BookingDetailError({ notFound, onBack, onRetry }: { notFound: boolean; onBack: () => void; onRetry: () => void }) {
  return (
    <FocusedScreenMessage
      testId="booking-detail-error"
      title={notFound ? 'Booking not found' : BOOKING_READ_FAILED.title}
      message={notFound ? "This booking may have been removed or you don't have access to it." : BOOKING_READ_FAILED.text}
      action={notFound ? { label: 'Back', onClick: onBack } : { label: BOOKING_READ_FAILED.retryLabel, onClick: onRetry }}
    />
  );
}

export interface BookingDetailCancel {
  open: boolean;
  /** The refund rule that applies now, read fresh when the window opens. */
  policy: { loading: boolean; error: boolean; line: string | null };
  pending: boolean;
  /** What was just cancelled: its done card shows until Done. */
  success: CancelKind | null;
  onOpen: () => void;
  onClose: () => void;
  onConfirm: () => void;
  onDone: () => void;
}

export interface BookingDetailViewProps {
  booking: BookingDetail;
  /** The business the chat row and the notices name; empty hides the chat row. */
  businessName: string;
  /** The single state-derived errand (Pay now, Complete booking, Book again); null for none. */
  errand: { label: string; onClick: () => void } | null;
  /** Add to Calendar's menu: the booking's Google and Outlook links, how a link opens (the website's are plain links), and Apple Calendar's file. */
  calendar: { choices: CalendarChoices; onOpen?: (url: string) => void; onApple: () => void };
  onJoinVirtual: () => void;
  onCopyPasscode: () => void;
  onWhereTap: (rect: DOMRect) => void;
  onChat: () => void;
  cancel: BookingDetailCancel;
}

export function BookingDetailView({
  booking, businessName, errand, calendar, onJoinVirtual, onCopyPasscode, onWhereTap, onChat, cancel,
}: BookingDetailViewProps) {
  const { status, service, provider } = booking;
  const d = new Date(booking.scheduledAt);
  const paymentRow = derivePaymentRow(booking);
  const heroWhen = heroWhenLabel(booking);
  const eyebrow = service.type === 'event' ? 'Your event' : isRequest(status) ? 'Your request' : 'Your session';
  // A waiting booking that owes only forms reads "Forms due" here as on its card and the Home hero (Gabriel 2026-10-07).
  const pill = statusPill(booking);
  const showWhere = status !== 'CANCELLED' && !isRequest(status);
  // service-by-phone D14: a by-phone request says By phone and who will call too.
  const showPhone = status !== 'CANCELLED' && booking.location.isByPhone;
  const phoneLine = phoneCallStep(booking.phoneCall)?.line ?? null;
  const showCalendarAction = status === 'CONFIRMED' || status === 'PENDING_PAYMENT';
  const showPrep = (status === 'CONFIRMED' || status === 'PENDING_PAYMENT') && !!service.preparationNotes;
  const showAbout = (status === 'CONFIRMED' || status === 'PENDING_PAYMENT' || status === 'COMPLETED') && !!service.description;
  const whenLabelText = status === 'CANCELLED' ? 'Was scheduled' : isRequest(status) ? 'Requested' : 'When';
  const whenSub =
    status === 'CANCELLED'
      ? `${service.duration} minutes${provider ? ` · with ${provider.name}` : ''}`
      : showCalendarAction
        ? `${service.duration} minutes · tap to add to calendar`
        : `${service.duration} minutes`;

  // The line for the booking's state (bookingNotice), shared with the website's booking pages.
  const line = bookingNotice(booking, businessName);
  const notice = line
    ? { icon: line.icon === 'info' ? <IconInfo /> : <IconClock />, node: line.bold ? <><b>{line.title}</b> {line.text}</> : `${line.title} ${line.text}` }
    : null;

  const isWaitlisted = status === 'WAITLISTED';
  const cancelLabel = isWaitlisted ? 'Leave the waitlist' : isRequest(status) ? 'Cancel this request' : 'Cancel this booking';

  return (
    <>
      <div className={`${styles.statusPill} ${pill.muted ? styles.muted : ''}`} data-testid="status-pill">
        <span className={styles.dot} />
        {pill.label}
      </div>

      <BookingHero
        imageUrl={service.imageUrl}
        focalPoint={{
          x: service.focalPointX ?? null,
          y: service.focalPointY ?? null,
          scale: service.focalPointScale ?? null,
        }}
        name={service.name}
        providerName={provider?.name ?? ''}
        providerPhoto={provider?.avatarUrl ?? null}
        whenLabel={heroWhen}
        eyebrow={eyebrow}
        emphasizeWhenLabel
      />

      <div className={`${styles.scroll} ${errand ? styles.hasFoot : ''}`}>
        <div className={styles.heroSpacer} aria-hidden="true" />
        <div className={styles.sheet}>
          <div className={styles.grab} aria-hidden="true" />
          <div className={styles.body}>
            {notice && (
              <div className={styles.notice} role="status">
                {notice.icon}
                <div className={styles.noticeText}>{notice.node}</div>
              </div>
            )}

            <div className={styles.det}>
              {/* When */}
              <div className={styles.row}>
                <IconCalendar />
                <div>
                  <div className={styles.lbl}>{whenLabelText}</div>
                  <div className={styles.val}>{`${fmtDayDate(d)} · ${fmtTime(d)}`}</div>
                  <div className={styles.sub}>{whenSub}</div>
                </div>
                {showCalendarAction && (
                  <CalendarMenu {...calendar} className={styles.rowBtn} ariaLabel="Add to calendar">
                    <IconPlus />
                  </CalendarMenu>
                )}
              </div>

              {/* Where: by phone, the call line with no map, no Join and no Change (service-by-phone D14) */}
              {showPhone && (
                <div className={styles.row}>
                  <IconPhone />
                  <div>
                    <div className={styles.lbl}>Where</div>
                    <div className={styles.val}>By phone</div>
                    {phoneLine && <div className={styles.sub}>{phoneLine}</div>}
                  </div>
                </div>
              )}
              {showWhere && !booking.location.isByPhone && (
                <div className={styles.row}>
                  {booking.location.isVirtual ? <IconGlobe /> : <IconPin />}
                  {booking.location.isVirtual ? (
                    <div className={`${styles.whereVirtualMain} ${booking.location.virtualLink ? '' : styles.whereVirtualMainFull}`}>
                      <button type="button" className={styles.whereText} onClick={onJoinVirtual}>
                        <div className={styles.lbl}>Where</div>
                        <div className={styles.val}>Online</div>
                        <div className={styles.sub}>{joinSub(booking.location.virtualPlatform)}</div>
                      </button>
                      {booking.location.virtualPassword && (
                        <button type="button" className={styles.copyChip} onClick={onCopyPasscode} aria-label="Copy passcode">
                          <span className={styles.copyMono}>Passcode {booking.location.virtualPassword}</span>
                          <IconCopy />
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.whereMain}
                      onClick={(e) => onWhereTap(e.currentTarget.getBoundingClientRect())}
                    >
                      <div>
                        <div className={styles.lbl}>Where</div>
                        <div className={styles.val}>{booking.location.name ?? 'Location'}</div>
                        <div className={styles.sub}>
                          {booking.location.address ?? ''}{booking.location.address ? ' · ' : ''}tap to open map
                        </div>
                      </div>
                      <IconChevron />
                    </button>
                  )}
                  {booking.location.isVirtual && booking.location.virtualLink && (
                    <button type="button" className={styles.rowBtn} onClick={onJoinVirtual} aria-label="Join session">
                      <IconVideo />
                    </button>
                  )}
                  {/* location-notes-to-client: the business's notes, outside the map tap */}
                  {booking.location.notes && <div className={styles.whereNote}>{booking.location.notes}</div>}
                </div>
              )}

              {/* Payment */}
              {paymentRow && (
                <div className={`${styles.row} ${paymentRow.ledger ? styles.top : ''}`}>
                  <IconCard />
                  <div className={styles.minw}>
                    <div className={styles.lbl}>Payment</div>
                    <div className={styles.val}>{paymentRow.val}</div>
                    {paymentRow.sub && <div className={styles.sub}>{paymentRow.sub}</div>}
                    {paymentRow.ledger && (
                      <div className={styles.ledger}>
                        {paymentRow.ledger.lines.map((line, i) => (
                          <div key={i} className={styles.lline}>
                            <div className={styles.what}>
                              Refund{' '}
                              <span className={`${styles.chip} ${line.onItsWay ? '' : styles.chipDone}`}>
                                {line.onItsWay ? 'On its way' : 'Refunded'}
                              </span>
                              <div className={styles.when}>{line.when}</div>
                            </div>
                            <div className={styles.money}>{`−${formatCurrency(line.amount)}`}</div>
                          </div>
                        ))}
                        {paymentRow.ledger.net != null && (
                          <div className={`${styles.lline} ${styles.net}`}>
                            <div className={styles.what}>Net paid</div>
                            <div className={styles.money}>{formatCurrency(paymentRow.ledger.net)}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {paymentRow.amount != null && (
                    <span className={`${styles.amt} ${paymentRow.amountDim ? styles.dim : ''}`}>{formatCurrency(paymentRow.amount)}</span>
                  )}
                </div>
              )}
            </div>

            {/* Prep card — admin rich text, renamable heading */}
            {showPrep && (
              <div className={styles.prepCard} data-testid="prep-card">
                <div className={styles.pcKicker}>{service.preparationNotesLabel || 'How to prepare'}</div>
                <SafeRichText html={service.preparationNotes} />
              </div>
            )}

            {/* About */}
            {showAbout && (
              <div>
                <div className={styles.secH}>About this {service.type === 'event' ? 'event' : 'session'}</div>
                <SafeRichText html={service.description} />
              </div>
            )}

            {/* Chat with the business: opens the chat screen's Secure room */}
            {businessName && (
              <ChatRow
                name={businessName}
                subtitle="Secure messaging"
                gradientAvatar
                avatarFallback={Array.from(businessName)[0]}
                onChat={onChat}
                testId="provider-card"
              />
            )}

            {/* Cancel link */}
            {booking.canCancel && (
              <div className={styles.cancelRow}>
                <button type="button" className={styles.cancelLink} onClick={cancel.onOpen}>
                  {cancelLabel}
                </button>
              </div>
            )}
            {/* booking-truth-batch: the authored window closed online cancellation */}
            {!booking.canCancel && booking.cancelClosedLine && (
              <div className={styles.cancelRow}>
                <p className={styles.cancelClosed}>{booking.cancelClosedLine}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Errand bar */}
      {errand && (
        <div className={styles.footBar}>
          <button type="button" className={styles.btnPrimary} onClick={errand.onClick}>
            {errand.label}
            <IconArrowRight />
          </button>
        </div>
      )}

      {/* Cancel confirmation sheet — shared ClientSheet (animates open AND close;
          the mutation's onSuccess/onError flip cancelOpen → the sheet exits). */}
      <ClientSheet
        open={cancel.open}
        onClose={cancel.onClose}
        dismissable={!cancel.pending}
        draggable
        kicker={isWaitlisted ? 'Leave waitlist' : isRequest(status) ? 'Cancel request' : 'Cancel booking'}
        title={isWaitlisted ? 'Leave this waitlist?' : isRequest(status) ? 'Cancel this request?' : 'Cancel this booking?'}
        testId="cancel-sheet"
      >
        <div className={sheetStyles.copy}>
          {isWaitlisted ? (
            <>
              You&apos;ll be removed from the waitlist for the <b>{fmtDayDate(d)}</b> {service.type === 'event' ? 'event' : 'session'}
              {provider ? <> with {provider.name}</> : null}. You won&apos;t be notified if a spot opens.
            </>
          ) : (
            <>
              Your <b>{fmtDayDate(d)}</b> {service.type === 'event' ? 'event' : 'session'}
              {provider ? <> with {provider.name}</> : null} will be cancelled. This can&apos;t be undone.
            </>
          )}
        </div>
        {service.type !== 'event' && (
          <div className={sheetStyles.nudge}>
            Need a different time instead? Cancel, then pick a new slot from the service page. It takes under a minute.
          </div>
        )}
        {cancel.policy.loading ? (
          <div className={styles.refundPolicy} role="status">Checking your refund policy…</div>
        ) : cancel.policy.error ? (
          <div className={styles.refundPolicy} role="note" aria-label="Refund policy">
            We couldn't check your refund policy right now.
          </div>
        ) : cancel.policy.line ? (
          <div className={styles.refundPolicy} role="note" aria-label="Refund policy">
            <IconRefund />
            <span>{cancel.policy.line}</span>
          </div>
        ) : null}
        <div className={sheetStyles.actions}>
          <button type="button" className={sheetStyles.keep} onClick={cancel.onClose} disabled={cancel.pending}>
            {isWaitlisted ? 'Stay on waitlist' : isRequest(status) ? 'Keep my request' : 'Keep my booking'}
          </button>
          <button type="button" className={sheetStyles.danger} onClick={cancel.onConfirm} disabled={cancel.pending}>
            {cancel.pending ? (isWaitlisted ? 'Leaving…' : 'Cancelling…') : isWaitlisted ? 'Yes, leave waitlist' : isRequest(status) ? 'Yes, cancel request' : 'Yes, cancel booking'}
          </button>
        </div>
      </ClientSheet>

      {cancel.pending && (
        <div className={styles.cancelFeedbackBackdrop} data-testid="cancel-progress">
          <div className={styles.cancelProgress} role="status" aria-live="polite">
            <span className={styles.cancelSpinner} aria-hidden="true" />
            <span>Cancelling…</span>
          </div>
        </div>
      )}

      {cancel.success && (
        <div className={styles.cancelFeedbackBackdrop}>
          <div className={styles.cancelSuccessCard} role="dialog" aria-modal="true" aria-labelledby="cancel-success-title">
            <div className={styles.cancelSuccessIcon} aria-hidden="true">✓</div>
            <h2 id="cancel-success-title">
              {cancel.success === 'waitlist' ? 'Waitlist left' : cancel.success === 'request' ? 'Request canceled' : 'Booking canceled'}
            </h2>
            <p>
              {cancel.success === 'waitlist'
                ? 'You have been removed from the waitlist.'
                : cancel.success === 'request'
                  ? 'Your booking request has been canceled.'
                  : 'Your booking has been canceled.'}
            </p>
            <button type="button" className={styles.cancelSuccessButton} onClick={cancel.onDone} autoFocus>
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}
